/**
 * UitInVlaanderenCredit — UiTdatabank source attribution.
 *
 * publiq's platform terms require every overview showing UiTdatabank events
 * to credit uitinvlaanderen.be as the source via a link (or logo + link),
 * and detail pages where possible. Like PoweredByGoogle, this is a licence
 * condition, not decoration — but unlike Google's badge it must only appear
 * when UitDatabank content is actually on screen (`hasUitdatabankSource`),
 * the same rule Tiqets photo credits follow.
 *
 * One attribution per overview surface suffices; the social feed uses the
 * 'inline' variant per event card instead, because an infinite mixed feed
 * has no footer the relevant cards are reliably near.
 *
 * Variants:
 *   - 'default' (py-3, centered)      — page/section footer
 *   - 'compact' (right-aligned, tiny) — under card strips; carries no margin
 *     of its own so a caller's margin class always wins (two margin-top
 *     utilities on one element resolve by stylesheet order, not class order)
 *   - 'inline'  (plain small line)    — inside a single event card
 */

import React from 'react';
import { useTranslation } from 'react-i18next';

import { focusStyle } from '../../styles/focus';

const UIT_URL = 'https://www.uitinvlaanderen.be';
const UIT_DOMAIN = 'uitinvlaanderen.be';
const UIT_SOURCE_TYPE = 'uitdatabank';

/**
 * Whether any of the given rows is UitDatabank content. Accepts both raw API
 * rows (`source_type`) and normalized card/show shapes (`source`), so callers
 * can test whatever list they already render from.
 */
export const hasUitdatabankSource = (items) =>
  (items || []).some(
    it => !!it && (it.source_type === UIT_SOURCE_TYPE || it.source === UIT_SOURCE_TYPE)
  );

function UitInVlaanderenCredit({ variant = 'default', className = '' }) {
  const { t } = useTranslation();
  const variants = {
    default: 'flex items-center justify-center py-3 text-xs text-content-muted',
    compact: 'flex items-center justify-end text-xs text-content-muted',
    inline: 'text-xs text-content-muted',
  };

  return (
    <p className={`${variants[variant] || variants.default} ${className}`}>
      <span>{t('uitInVlaanderenCredit.prefix')}&nbsp;</span>
      <a
        href={UIT_URL}
        target="_blank"
        rel="noopener noreferrer"
        // Cards that host the inline variant are themselves clickable.
        onClick={(e) => e.stopPropagation()}
        className={`text-primary hover:underline ${focusStyle} rounded-lg`}
        aria-label={t('uitInVlaanderenCredit.linkAriaLabel')}
      >
        {UIT_DOMAIN}
      </a>
    </p>
  );
}

export default UitInVlaanderenCredit;
